import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { SaleTemplate } from '../../models/media/sale-template.model';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { SaleTemplateCategoriesPopupComponent } from '../sale-template-categories-popup/sale-template-categories-popup.component';

@Component({
  selector: 'app-sale-template',
  templateUrl: './sale-template.component.html',
  styleUrls: ['./sale-template.component.scss']
})
export class SaleTemplateComponent extends BaseUnsubscribeComponent implements OnInit {
  @Input() public saleTemplateId?: number = null;
  public dataSource: MatTableDataSource<SaleTemplate>;
  public isLoader: boolean;
  public closeResult = '';
  private _searchText: string = '';
  isSalesCompany: boolean = this.authService.IsSalesCompany();
  public displayedColumns$ = new BehaviorSubject([
    'name',
    'description',
    'categoryCount',
    'action'
  ]);
  private sortOrderSubject$ = new BehaviorSubject<'ASC' | 'DESC'>('ASC');
  @ViewChild("saleTemplateSort") sort: MatSort;
  constructor(private readonly settings: SettingsService, private readonly modalService: NgbModal, private readonly authService: AuthService) {
    super();
  }

  openSaleTemplateCategoriesPopup(template?: SaleTemplate) {
    if (!template) {
      template = {
        saleTemplateId: 0,
        name: '',
        description: '',
        createdBy: 0,
        createdDate: new Date(),
        isActive: true
      };
    }
    const modalRef = this.modalService.open(SaleTemplateCategoriesPopupComponent, {
      ariaLabelledBy: template.saleTemplateId + 'Id',
      centered: true,
      size: 'xl',
      keyboard: false,
      backdrop: 'static',
    });

    modalRef.componentInstance.data = { template: template };
    modalRef.result.then(
      (result) => {
        this.loadSaleTemplates();
      },
      (reason) => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }


  public loadSaleTemplates(): void {
    this.isLoader = true;
    this.settings.getSaleTemplates().pipe(takeUntil(this.unsubscribe$)).subscribe((response) => {
      this.isLoader = false;
      this.dataSource = new MatTableDataSource<SaleTemplate>(response);
      if (this._searchText) {
        this.applyFilter(this._searchText);
      }
    });
  }

  ngOnInit(): void {
    this.loadSaleTemplates();
  }

  public sortTable(sortBy: keyof SaleTemplate): void {
    const currentOrder = this.sortOrderSubject$.value;
    this.sortOrderSubject$.next(currentOrder === 'ASC' ? 'DESC' : 'ASC');
    this.compare(sortBy);
  }

  private compare(keySorted: keyof SaleTemplate): void {
    const categories = this.dataSource.data;

    categories.sort((a, b) =>
      this.sortOrderSubject$.value === 'ASC'
        ? this.sortValuesByName(a, b, keySorted)
        : this.sortValuesByName(b, a, keySorted) // Reverse order for DESC
    );

    this.dataSource.data = categories; // Update the data source
  }

  private sortValuesByName(
    a: SaleTemplate,
    b: SaleTemplate,
    keySorted: keyof SaleTemplate
  ): number {
    const aValue = a[keySorted] || '';
    const bValue = b[keySorted] || '';

    return aValue.toString().localeCompare(bValue.toString(), navigator.languages[0] || navigator.language, {
      numeric: true,
      ignorePunctuation: true,
    });
  }


  // Method to filter table data
  applyFilter(filterValue: string) {
    this._searchText = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
